import Tooltip from '@mui/material/Tooltip';
import React, { createRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { wordApi } from 'api';
import { useModal } from 'shared/hooks';
import { TaskKeysType } from 'shared/types';
import { ItemActionModal } from 'shared/ui/ItemActionModal';

import wordRedSvg from '../../../assets/img/file-doc-red.svg';
import wordSvg from '../../../assets/img/file-doc.svg';

interface Props {
  currentICLId: string;
  id: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  wordUrl?: string;
  remoteTaskId?: string;
  wordFile: { file: File; url: string } | null;
  setWordFile: (file: { file: File; url: string } | null) => void;
  hasText?: boolean;
  closeModal?: () => void;
}

export const WordInput = ({
  currentICLId,
  id,
  handleChangeTaskByKey,
  wordUrl,
  remoteTaskId,
  wordFile,
  setWordFile,
  hasText,
  closeModal,
}: Props) => {
  const { t } = useTranslation();
  const wordInputRef = createRef<HTMLInputElement>();
  const [isWordLoading, setIsWordLoading] = useState(false);
  const { isOpen: isRemovingWordModalOpen, close: closeRemovingWordModal, open: openRemovingWordModal } = useModal();

  const handleAddWordLocal = async () => {
    const wordInput = wordInputRef.current;

    if (!wordInput || !wordInput.files) {
      return;
    }

    const file = wordInput.files[0];
    setWordFile({ file, url: URL.createObjectURL(file) });
  };

  const sendWord = async () => {
    const wordInput = wordInputRef.current;

    if (!wordInput || !wordInput.files) {
      return;
    }

    setIsWordLoading(true);

    const file = wordInput.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const gottenWordUrl = await wordApi.addWord(file, currentICLId, id);
      handleChangeTaskByKey(id, 'wordUrl', gottenWordUrl);

      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsWordLoading(false);
  };

  const removeWord = async () => {
    setIsWordLoading(true);

    try {
      remoteTaskId && (await wordApi.removeWord(wordUrl ?? (wordFile?.url as string), currentICLId, id));
      handleChangeTaskByKey(id, 'wordUrl', '');
      setWordFile(null);
      toast.success(t('Successfully removed!'));
      closeRemovingWordModal();
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsWordLoading(false);
  };

  return (
    <div className="ql-image button-icon">
      <Tooltip
        title={wordUrl || wordFile ? t('remove word') : t('add word')}
        componentsProps={{
          popper: {
            sx: {
              ['& .MuiTooltip-arrow']: {
                color: '#9CDFFC',
              },
              ['& .MuiTooltip-tooltip']: {
                backgroundColor: '#9CDFFC',
                color: '#000000',
                fontSize: '14px',
                padding: '14px',
                borderRadius: '8px',
              },
            },
          },
        }}
        arrow
      >
        <button
          onClick={() => {
            closeModal && closeModal();

            if (wordUrl || wordFile) {
              openRemovingWordModal();
            } else {
              wordInputRef.current?.click();
            }
          }}
          className={
            hasText
              ? 'flex items-center gap-[10px]'
              : 'btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600'
          }
        >
          {wordUrl || wordFile ? <img src={wordRedSvg} alt="camera" /> : <img src={wordSvg} alt="camera" />}

          <input
            id="wordInput"
            type="file"
            className="hidden"
            accept="application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ref={wordInputRef}
            onChange={() => (remoteTaskId ? sendWord() : handleAddWordLocal())}
            disabled={isWordLoading || !!wordUrl}
            title={t('add word')}
          />

          {hasText && (
            <p className="!mb-0 text-base font-bold text-[#242C2C]">
              {wordUrl || wordFile ? t('remove word') : t('add word')}
            </p>
          )}
        </button>
      </Tooltip>

      <ItemActionModal
        isOpen={isRemovingWordModalOpen}
        close={closeRemovingWordModal}
        title={t('remove word')}
        onHandle={removeWord}
        isSubmitButtonDisabled={isWordLoading}
        buttonTitle="delete"
      />
    </div>
  );
};
